/**
 * SyncQueuePanel — Drawer que lista todas as ações pendentes na fila offline
 * com status individual, opção de reenviar e descartar.
 *
 * Integra com o OfflineSyncBadge existente — pode ser aberto de lá
 * ou independentemente.
 *
 * Usage:
 *   <SyncQueuePanel open={open} onOpenChange={setOpen} />
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertTriangle,
  Check,
  CloudOff,
  RefreshCw,
  Trash2,
  Cloud,
  Loader2,
} from 'lucide-react';
import { getAllQueuedActions, removeFromQueue, type OfflineAction } from '@/lib/offlineDb';
import { syncQueue } from '@/lib/offlineQueue';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS, es, fr, de, ja } from 'date-fns/locale';
import type { Locale } from 'date-fns/locale';

const localeMap: Record<string, Locale> = {
  'pt-BR': ptBR,
  en: enUS,
  es,
  fr,
  de,
  ja,
};

/** Friendly table name map */
const tableLabels: Record<string, string> = {
  presencas: 'Presença',
  alunos: 'Aluno',
  turmas: 'Turma',
  crm_interacoes: 'Interação CRM',
  crm_professor_interacoes: 'Interação Professor',
  evolucao_tecnica: 'Avaliação Técnica',
  feedback_tecnico: 'Feedback Técnico',
  aulas_especiais: 'Aula Especial',
  eventos_alunos: 'Inscrição Evento',
};

const actionLabels: Record<string, string> = {
  insert: 'Criar',
  update: 'Atualizar',
  upsert: 'Salvar',
};

interface SyncQueuePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SyncQueuePanel({ open, onOpenChange }: SyncQueuePanelProps) {
  const { t, i18n } = useTranslation();
  const [actions, setActions] = useState<OfflineAction[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const locale = localeMap[i18n.language] ?? ptBR;

  const loadActions = useCallback(async () => {
    try {
      const all = await getAllQueuedActions();
      setActions(all.sort((a, b) => b.createdAt - a.createdAt));
    } catch {
      setActions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      loadActions();
    }
  }, [open, loadActions]);

  // Listen for queue changes
  useEffect(() => {
    const handler = () => loadActions();
    window.addEventListener('offline-queue-changed', handler);
    return () => window.removeEventListener('offline-queue-changed', handler);
  }, [loadActions]);

  const handleSyncAll = async () => {
    if (isSyncing || !navigator.onLine) return;
    setIsSyncing(true);
    try {
      await syncQueue();
      await loadActions();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDiscard = async (id: string) => {
    await removeFromQueue(id);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
    await loadActions();
  };

  const pendingCount = actions.filter((a) => a.status === 'pending').length;
  const failedCount = actions.filter((a) => a.status === 'failed').length;
  const syncingCount = actions.filter((a) => a.status === 'syncing').length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" aria-hidden="true" />
            {t('sync.queueTitle', 'Fila de Sincronização')}
          </SheetTitle>
          <SheetDescription>
            {t('sync.queueDescription', 'Ações salvas localmente aguardando envio ao servidor.')}
          </SheetDescription>
        </SheetHeader>

        {/* Summary badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {pendingCount > 0 && (
            <Badge variant="outline" className="gap-1 text-amber-600 border-amber-300">
              <CloudOff className="h-3 w-3" />
              {pendingCount} {t('sync.pending', 'pendente' + (pendingCount > 1 ? 's' : ''))}
            </Badge>
          )}
          {syncingCount > 0 && (
            <Badge variant="outline" className="gap-1 text-primary border-primary/30">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {syncingCount} {t('sync.syncingCount', 'sincronizando')}
            </Badge>
          )}
          {failedCount > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              {failedCount} {t('sync.failedCount', 'com falha')}
            </Badge>
          )}
          {actions.length === 0 && !isLoading && (
            <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
              <Check className="h-3 w-3" />
              {t('sync.allSynced', 'Tudo sincronizado')}
            </Badge>
          )}
        </div>

        {/* Sync all button */}
        {actions.length > 0 && (
          <Button
            onClick={handleSyncAll}
            disabled={isSyncing || !navigator.onLine}
            className="w-full mb-4 gap-2"
            size="sm"
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {isSyncing
              ? t('sync.syncingAll', 'Sincronizando...')
              : t('sync.syncAll', 'Sincronizar tudo agora')}
          </Button>
        )}

        {/* Action list */}
        <ScrollArea className="h-[calc(100vh-280px)]">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : actions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3">
              <Check className="h-10 w-10 text-green-500" />
              <p className="text-sm">{t('sync.emptyQueue', 'Nenhuma ação pendente.')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {actions.map((action) => (
                <QueueActionCard
                  key={action.id}
                  action={action}
                  locale={locale}
                  onDiscard={() => handleDiscard(action.id)}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/** Individual action card */
function QueueActionCard({
  action,
  locale,
  onDiscard,
}: {
  action: OfflineAction;
  locale: Locale;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const tableName = tableLabels[action.table] ?? action.table;
  const actionName = actionLabels[action.action] ?? action.action;
  const timeAgo = formatDistanceToNow(new Date(action.createdAt), {
    addSuffix: true,
    locale,
  });

  // Extract a readable label from payload
  const payloadLabel = getPayloadLabel(action.payload);

  return (
    <div className="flex items-start gap-3 rounded-lg border p-3 bg-card">
      <SyncStatusIndicator status={action.status} size={18} />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">
            {actionName} {tableName}
          </span>
          {action.retries > 0 && (
            <span className="text-xs text-muted-foreground">
              ({action.retries}x {t('sync.retried', 'tentativas')})
            </span>
          )}
        </div>
        
        {payloadLabel && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {payloadLabel}
          </p>
        )}
        
        <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>
        
        {action.errorMessage && (
          <p className="text-xs text-destructive mt-1 truncate" title={action.errorMessage}>
            {action.errorMessage}
          </p>
        )}
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={onDiscard}
        title={t('sync.discard', 'Descartar')}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/** Extract a human-readable label from the action payload */
function getPayloadLabel(payload: Record<string, unknown> | Record<string, unknown>[]): string | null {
  const obj = Array.isArray(payload) ? payload[0] : payload;
  if (!obj) return null;

  // Try common name fields
  for (const key of ['nome', 'name', 'titulo', 'title', 'email', 'descricao']) {
    if (typeof obj[key] === 'string' && obj[key]) {
      return obj[key] as string;
    }
  }
  return null;
}
